import React, { useEffect, useMemo, useRef, useState } from 'react';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import { getEligibleBespokeModuleGroups, normalizeTeamMetadata } from '../../teams/utils/teamMetadata';
import {
  enabledAtPath as moduleEnabledAtPath,
  getModuleSections,
  withModuleDefaults,
} from '../../teams/utils/moduleUtils';

/* ───────── Component ───────── */
export default function EditModulesForm({ isOpen, onClose, onSave, initialModules, metadata = {}, isFullAdmin = false }) {
  const [localModules, setLocalModules] = useState(withModuleDefaults(initialModules || {}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const localModulesRef = useRef(withModuleDefaults(initialModules || {}));

  useEffect(() => {
    if (isOpen) {
      const next = withModuleDefaults(initialModules || {});
      localModulesRef.current = next;
      setLocalModules(next);
      setSaving(false);
      setError('');
    }
  }, [isOpen, initialModules]);

  const writeAtPath = (obj, path, updater) => {
    if (!path || path.length === 0) return obj;
    const recur = (node, idx) => {
      const key = path[idx];
      if (idx === path.length - 1) {
        const prevLeaf = node?.[key] ?? {};
        return { ...node, [key]: updater(prevLeaf) };
      }
      const child = node?.[key] ?? {};
      return { ...node, [key]: recur(child, idx + 1) };
    };
    return recur(obj || {}, 0);
  };

  const enabledAtPath = (path) => moduleEnabledAtPath(localModules, path);
  const enabledAtPathFrom = (obj, path = []) => moduleEnabledAtPath(obj, path);
  const messagingPath = ['communications', 'messages'];
  const legacyMessagingPath = ['messages'];
  const inboxBroadcastsPath = ['communications', 'inboxBroadcasts'];

  // Read enabled with legacy fallback (for Messaging)
  const isEnabledAt = (obj, path, legacyPath) => {
    if (enabledAtPathFrom(obj, path)) return true;
    if (legacyPath && enabledAtPathFrom(obj, legacyPath)) return true;
    return false;
  };
  const isEnabled = (path, legacyPath) => isEnabledAt(localModules, path, legacyPath);

  const setMessagingEnabled = (modules, enabled) => {
    let next = writeAtPath(modules, messagingPath, (leaf) => ({ ...leaf, enabled }));
    next = writeAtPath(next, legacyMessagingPath, (leaf) => ({ ...leaf, enabled }));
    return next;
  };

  const togglePath = (path, legacyPath) => {
    setLocalModules((prev) => {
      const next = { ...prev };
      const currentEnabled = isEnabledAt(prev, path, legacyPath);
      const newEnabled = !currentEnabled;
      let applied = writeAtPath(next, path, (leaf) => ({ ...leaf, enabled: newEnabled }));

      // Keep legacy and new paths in sync while legacy fallback exists.
      if (legacyPath) {
        applied = writeAtPath(applied, legacyPath, (leaf) => ({ ...leaf, enabled: newEnabled }));
      }

      const isMessagingToggle = path.join('.') === messagingPath.join('.');
      const isInboxBroadcastsToggle = path.join('.') === inboxBroadcastsPath.join('.');
      const inboxBroadcastsEnabled = enabledAtPathFrom(prev, inboxBroadcastsPath);

      if (isInboxBroadcastsToggle && newEnabled) {
        applied = setMessagingEnabled(applied, true);
      }

      if (isMessagingToggle && !newEnabled && inboxBroadcastsEnabled) {
        applied = setMessagingEnabled(applied, true);
      }

      localModulesRef.current = applied;
      return applied;
    });
  };

  const eligibleBespokeGroups = useMemo(() => getEligibleBespokeModuleGroups(metadata), [metadata]);
  const hasSelectedMetadataLabels = useMemo(() => normalizeTeamMetadata(metadata).labels.length > 0, [metadata]);

  const sections = useMemo(() => {
    return getModuleSections(eligibleBespokeGroups);
  }, [eligibleBespokeGroups]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave(localModulesRef.current);
    } catch (saveError) {
      setError(saveError?.message || 'Could not save enabled modules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <form onSubmit={handleSubmit} className="modal-form">
      <h2>Edit Enabled Modules</h2>
      {error && <ErrorMessage message={error} />}

      {sections.map(({ key, title, items }) => (
        <div key={key} style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0.25rem 0 0.5rem', fontSize: '1.05rem' }}>{title}</h3>
          {items.map(({ path, legacyPath, label, description }) => {
            const id = path.join('.');
            const isMessagingToggle = path.join('.') === messagingPath.join('.');
            const isFullAdminOnly = id === 'demographics';
            const inboxBroadcastsEnabled = enabledAtPath(inboxBroadcastsPath);
            return (
              <div key={id} className="form-toggle-row" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <span className="form-toggle-label" style={{ display: 'block', fontWeight: 600 }}>
                    {label}
                  </span>
                  <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {description}{isFullAdminOnly && !isFullAdmin ? ' A full club admin must change this module.' : ''}
                  </span>
                </div>
                <label className="toggle-switch" aria-label={`Toggle ${label}`}>
                  <input
                    type="checkbox"
                    checked={isEnabled(path, legacyPath)}
                    disabled={saving || (isMessagingToggle && inboxBroadcastsEnabled) || (isFullAdminOnly && !isFullAdmin)}
                    onChange={() => togglePath(path, legacyPath)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            );
          })}
          {key === 'communication' && enabledAtPath(inboxBroadcastsPath) && (
            <p className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Inbox Broadcasts depends on Messaging, so Messaging stays enabled while broadcasts are on.
            </p>
          )}
        </div>
      ))}

      {!hasSelectedMetadataLabels && eligibleBespokeGroups.length === 0 && (
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Select club categories or types in <strong>Club Details</strong> to reveal eligible bespoke modules.
        </p>
      )}

      <div className="form-actions">
        <SubmitButton
          type="button"
          onClick={onClose}
          label="Cancel"
          variant="ghost"       // cleaner cancel style
          size="sm"
          disabled={saving}
        />
        <SubmitButton
          type="submit"
          label={saving ? 'Saving…' : 'Save Changes'}
          variant="primary"     // use new unified styling
          disabled={saving}
        />
      </div>
    </form>
  );
}
